'use client';

/* eslint-disable tailwindcss/no-arbitrary-value */
import {
  Popover,
  PopoverContent,
  PopoverHandler,
} from '@material-tailwind/react';
import { DateRangeCalendar } from './components/DateRangeCalendar';
import { DateRangeSheet } from './components/DateRangeSheet';
import {
  defaultContentClassName,
  defaultPlacement,
  defaultTriggerButtonClassName,
  defaultTriggerWrapperClassName,
} from './constants';
import { useDateRangePicker } from './hooks';
import {
  DateRangePickerStaticText,
  type DateRangePickerStaticTextType,
} from './i18n';
import { DateRangePickerProps } from './types';
import DateRangeIcon from '$/components/icons/DateRange';
import { Portal } from '$/components/Portal';
import { useClientContext } from '$/modules/context/client';
import { getClientTranslations } from '$/modules/i18n/client.index';

export function DateRangePicker(props: DateRangePickerProps): JSX.Element {
  const {
    startDate,
    endDate,
    displayVariant,
    minDate,
    maxDate,
    placeholder,
    onSelect,
    onSelectionStart,
    onUnavailableClick,
    duration,
    size = 'full',
    mode = 'range',
  } = props;

  const context = useClientContext();
  const staticText = getClientTranslations<DateRangePickerStaticTextType>(
    DateRangePickerStaticText,
    context,
  );

  const {
    isOpen,
    isSheetLayout,
    desktopDayPickerProps,
    resolvedPlaceholder,
    resolvedConfirmLabel,
    isSelectionValid,
    guidanceText,
    footerState,
    unavailableReasonText,
    hasSelectedDates,
    selectedDateLabel,
    startDateLabel,
    endDateLabel,
    rangeSeparator,
    isRangeSelected,
    handlePopoverHandler,
    handleTriggerClick,
    handleSheetClose,
    handleDoneClick,
    dayPickerProps,
    sheetDayPickerProps,
    sheetScrollToMonthIndex,
    sheetSummary,
    sheetWeekdayNames,
    canGoToPreviousMonth,
    canGoToNextMonth,
    handlePreviousMonthClick,
    handleNextMonthClick,
  } = useDateRangePicker({
    startDate,
    endDate,
    minDate,
    maxDate,
    placeholder,
    onSelect,
    onSelectionStart,
    onUnavailableClick,
    duration,
    size,
    mode,
    displayVariant,
    lang: context.lang,
    staticText,
  });

  const effectiveTriggerWrapperClassName = defaultTriggerWrapperClassName;
  const effectiveTriggerButtonClassName = defaultTriggerButtonClassName;

  const renderIcon = (
    <DateRangeIcon className="mr-2 size-shapes/icon-size-lg text-gray-600" />
  );

  const triggerContent = (
    <>
      {renderIcon}
      {hasSelectedDates ? (
        <div className="max-w-full truncate">
          {selectedDateLabel ? (
            <span className="text-text/primary">{selectedDateLabel}</span>
          ) : (
            <>
              {startDateLabel && (
                <span className="whitespace-nowrap text-text/primary">
                  {startDateLabel}
                </span>
              )}
              {endDateLabel && (
                <>
                  <span className="text-text/primary">{rangeSeparator}</span>
                  <span className="whitespace-nowrap text-text/primary">
                    {endDateLabel}
                  </span>
                </>
              )}
            </>
          )}
        </div>
      ) : (
        <span className="text-text/disabled">{resolvedPlaceholder}</span>
      )}
    </>
  );

  // Below the desktop breakpoint the calendar is a full-screen sheet rendered
  // through a portal, not popover content: it needs neither the popover's
  // positioning nor its outside-click dismiss, and the ticket requires closing
  // through the ✕ only (CMS-1854 §3).
  if (isSheetLayout) {
    return (
      <>
        <div className={effectiveTriggerWrapperClassName}>
          <button
            aria-expanded={isOpen}
            className={effectiveTriggerButtonClassName}
            type="button"
            onClick={handleTriggerClick}
          >
            {triggerContent}
          </button>
        </div>
        {isOpen && (
          <Portal>
            <DateRangeSheet
              closeLabel={staticText.sheetClose}
              confirmLabel={resolvedConfirmLabel}
              dayPickerProps={sheetDayPickerProps}
              guidanceText={guidanceText}
              isConfirmDisabled={!isSelectionValid}
              isRangeSelected={isRangeSelected}
              onClose={handleSheetClose}
              onConfirmClick={handleDoneClick}
              scrollToMonthIndex={sheetScrollToMonthIndex}
              summary={sheetSummary}
              title={staticText.sheetTitle}
              unavailableReasonText={unavailableReasonText}
              weekdayNames={sheetWeekdayNames}
            />
          </Portal>
        )}
      </>
    );
  }

  const dateRangeContent = (
    <DateRangeCalendar
      canGoToNextMonth={canGoToNextMonth}
      canGoToPreviousMonth={canGoToPreviousMonth}
      dayPickerProps={dayPickerProps}
      desktopDayPickerProps={desktopDayPickerProps}
      confirmLabel={resolvedConfirmLabel}
      footerState={footerState}
      guidanceText={guidanceText}
      isConfirmDisabled={!isSelectionValid}
      isRangeSelected={isRangeSelected}
      nextMonthLabel={staticText.nextMonthLabel}
      onConfirmClick={handleDoneClick}
      onNextMonthClick={handleNextMonthClick}
      onPreviousMonthClick={handlePreviousMonthClick}
      previousMonthLabel={staticText.previousMonthLabel}
      unavailableReasonText={unavailableReasonText}
    />
  );

  return (
    <Popover
      open={isOpen}
      handler={handlePopoverHandler}
      placement={defaultPlacement}
    >
      <PopoverHandler>
        <div className={effectiveTriggerWrapperClassName}>
          <button
            className={effectiveTriggerButtonClassName}
            type="button"
            onClick={handleTriggerClick}
          >
            {triggerContent}
          </button>
        </div>
      </PopoverHandler>
      <PopoverContent className={defaultContentClassName}>
        {dateRangeContent}
      </PopoverContent>
    </Popover>
  );
}
